import { memo } from 'react';
import type { CellAggregateValues } from '../hooks/useCellSelection';

const numberFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 4,
  useGrouping: true,
});

function fmt(value: number | null): string {
  if (value === null) return '—';
  return numberFormatter.format(value);
}

function Divider() {
  return <div className="w-px h-3 bg-border mx-2" />;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex items-center gap-1">
      {/* CH-25: the label ("Count"/"Sum"/"Avg"/...) is fixed UI chrome and
          stays non-selectable; the computed value is data a user may want
          to copy (e.g. into a report) and must stay selectable. */}
      <span className="text-muted-foreground select-none">{label}:</span>
      <span className="text-foreground font-medium">{value}</span>
    </span>
  );
}

type CellAggregateBarProps = {
  aggregates: CellAggregateValues | null;
};

export const CellAggregateBar = memo(function CellAggregateBar({
  aggregates,
}: CellAggregateBarProps) {
  if (!aggregates || aggregates.count < 2 || aggregates.numericCount === 0) return null;

  return (
    <div
      className="flex-shrink-0 flex items-center gap-0 px-3 py-1 bg-muted/30 border-t border-border text-[12px]"
      data-testid="cell-aggregate-bar"
    >
      <Stat label="Count" value={String(aggregates.count)} />
      <Divider />
      <Stat label="Sum" value={fmt(aggregates.sum)} />
      <Divider />
      <Stat label="Avg" value={fmt(aggregates.avg)} />
      <Divider />
      <Stat label="Min" value={fmt(aggregates.min)} />
      <Divider />
      <Stat label="Max" value={fmt(aggregates.max)} />
    </div>
  );
});
